import styles from './index.module.css'

import { IoChevronDown } from '@react-icons/all-files/io5/IoChevronDown'
import { IoChevronForward } from '@react-icons/all-files/io5/IoChevronForward'
import { IoStar } from '@react-icons/all-files/io5/IoStar'
import { IoStarOutline } from '@react-icons/all-files/io5/IoStarOutline'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import api from '@disco/api/server'
import { SOURCE_SCOPE } from '@disco/constants/search'
import {
  selectSearchPage,
  setMdsCatalogs,
  setSourceScope,
} from '@disco/state-management/reducers/search-page'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import type { MdsCatalog } from 'types/search-page'

import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  IconButton,
  Link,
  ScrollArea,
  SearchTextField,
  Separator,
  Spinner,
  Text,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { CatalogInfoPopover } from '../catalog-info-popover'

import {
  FAKE_CATEGORIES,
  filterFakeCatalogs,
  findFakeCatalog,
  setFakeFavorite,
} from './fake-catalogs'

// PROTOTYPE: demo with fake catalogs. Set to false to use the real discovery
// panel API (which also returns non-MDS catalogs).
const USE_FAKE_CATALOGS = true

type PickerCatalog = MdsCatalog & {
  is_favorite?: boolean
  categories?: string[]
  description?: string
  track_count?: number
}

const DISCOVER_MUSIC_FAVORITES_URL = '/discover?filter=is_favorite'
const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

const toCatalog = (item: ClientLibraryDiscoveryPanelItem): PickerCatalog => ({
  id: item.id,
  display_name: item.display_name,
  image_logo_url: item.image_logo_url,
  is_favorite: item.is_favorite,
})

// PROTOTYPE: the discovery panel list also returns limited-access and private
// catalogs, which MDS doesn't search. Drop them when the field is present.
const isMdsCatalog = (item: ClientLibraryDiscoveryPanelItem): boolean =>
  !item.discovery_access_level ||
  item.discovery_access_level === 'discovery_easy_access'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * PROTOTYPE: picks which "Search across DISCO" catalogs to search in. The
 * choice is saved per user but not sent to the search API yet.
 */
export const MdsCatalogPicker = ({ open, onOpenChange }: Props) => {
  const dispatch = useDispatch()
  const { mdsCatalogs, sourceScope } = useSelector(selectSearchPage)

  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [isSelectedOpen, setIsSelectedOpen] = useState(true)
  const [selected, setSelected] = useState<
    Map<PickerCatalog['id'], PickerCatalog>
  >(new Map())
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState<PickerCatalog[]>([])
  const [loading, setLoading] = useState(false)
  // Star toggles made in the modal, so the list doesn't need a refetch.
  const [favoriteOverrides, setFavoriteOverrides] = useState<
    Map<PickerCatalog['id'], boolean>
  >(new Map())

  useEffect(() => {
    if (!open) return
    setSelected(
      new Map(
        (mdsCatalogs ?? []).map(c => [
          c.id,
          (USE_FAKE_CATALOGS && findFakeCatalog(c.id)) || c,
        ]),
      ),
    )
    setFavoritesOnly(false)
    setIsSelectedOpen(true)
    setQuery('')
    setCategory(null)
    setDebouncedQuery('')
    setFavoriteOverrides(new Map())
  }, [open, mdsCatalogs])

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedQuery(query),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timeout)
  }, [query])

  useEffect(() => {
    if (!open) return
    if (USE_FAKE_CATALOGS) {
      setResults(
        filterFakeCatalogs(debouncedQuery, favoritesOnly, category, PAGE_SIZE),
      )
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    api
      .getClientLibraryDiscoveryPanel(
        debouncedQuery,
        favoritesOnly ? 'is_favorite' : null,
        PAGE_SIZE,
      )
      .then(response => response.results.filter(isMdsCatalog).map(toCatalog))
      .then(catalogs => {
        if (!cancelled) setResults(catalogs)
      })
      .catch(() => {
        if (!cancelled) setResults([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, favoritesOnly, debouncedQuery, category])

  // Like the tag filter menu: checked catalogs sit in their own section at the
  // top, narrowed by search only, so the chips never hide a pick.
  const selectedList = useMemo(() => {
    const needle = debouncedQuery.toLowerCase()
    return [...selected.values()].filter(
      c =>
        !needle ||
        c.display_name?.toLowerCase().includes(needle) ||
        c.categories?.some(name => name.toLowerCase().includes(needle)),
    )
  }, [selected, debouncedQuery])

  const unselectedResults = useMemo(
    () => results.filter(c => !selected.has(c.id)),
    [results, selected],
  )

  const isFavorite = (catalog: PickerCatalog): boolean =>
    favoriteOverrides.get(catalog.id) ?? Boolean(catalog.is_favorite)

  const toggleSelected = useCallback((catalog: PickerCatalog) => {
    setSelected(prev => {
      const next = new Map(prev)
      if (next.has(catalog.id)) next.delete(catalog.id)
      else next.set(catalog.id, catalog)
      return next
    })
  }, [])

  const toggleFavorite = (catalog: PickerCatalog) => {
    const next = !isFavorite(catalog)
    setFavoriteOverrides(prev => new Map(prev).set(catalog.id, next))
    if (USE_FAKE_CATALOGS) {
      setFakeFavorite(catalog.id, next)
      return
    }
    api.clientLibraryDiscoveryPanelFavorite(catalog.id, next).catch(() => {
      setFavoriteOverrides(prev => new Map(prev).set(catalog.id, !next))
    })
  }

  const handleApply = () => {
    const onlySelected = selected.size > 0
    dispatch(
      setMdsCatalogs(
        onlySelected
          ? [...selected.values()].map(
              ({ id, display_name, image_logo_url }) => ({
                id,
                display_name,
                image_logo_url,
              }),
            )
          : null,
      ),
    )
    // Picking catalogs only makes sense with MDS on, so turn it on for the user.
    if (onlySelected && !sourceScope?.includes(SOURCE_SCOPE.MDS)) {
      dispatch(setSourceScope([SOURCE_SCOPE.BUSINESS, SOURCE_SCOPE.MDS]))
    }
    onOpenChange(false)
  }

  const emptyMessage =
    favoritesOnly && !debouncedQuery && !category
      ? 'No favorite catalogs yet. Star catalogs in All or in Discover Music to add them here.'
      : 'No catalogs found.'

  const renderRow = (catalog: PickerCatalog) => {
    const name = catalog.display_name?.trim() || 'Untitled catalog'
    const favorite = isFavorite(catalog)
    return (
      <Flex key={catalog.id} align="center" gap="3" className={styles.row}>
        <Text as="label" size="2" className={styles.rowLabel}>
          <Flex align="center" gap="3">
            <Checkbox
              checked={selected.has(catalog.id)}
              onCheckedChange={() => toggleSelected(catalog)}
            />
            <Avatar
              size="1"
              radius="small"
              src={catalog.image_logo_url ?? undefined}
              fallback={name.charAt(0).toUpperCase()}
            />
            <Flex direction="column" className={styles.nameColumn}>
              <span className={styles.name}>{name}</span>
              {catalog.categories?.length ? (
                <Text size="1" color="gray" className={styles.name}>
                  {catalog.categories.join(' · ')}
                </Text>
              ) : null}
            </Flex>
          </Flex>
        </Text>
        {/* Same hover card as the /search Source column. */}
        <CatalogInfoPopover
          source={{
            id: catalog.id,
            name,
            description: catalog.description,
            track_count: catalog.track_count,
            image_url: catalog.image_logo_url ?? undefined,
          }}
        >
          <span
            className={styles.infoIcon}
            aria-label={`About ${name}`}
            tabIndex={0}
          >
            <RxInfoCircled />
          </span>
        </CatalogInfoPopover>
        <IconButton
          variant="ghost"
          color={favorite ? 'amber' : 'gray'}
          size="1"
          aria-label={favorite ? `Unfavorite ${name}` : `Favorite ${name}`}
          onClick={() => toggleFavorite(catalog)}
        >
          {favorite ? <IoStar /> : <IoStarOutline />}
        </IconButton>
      </Flex>
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="520px" onClick={e => e.stopPropagation()}>
        <Dialog.Title>Choose catalogs</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="3">
          Pick which catalogs from across DISCO to include when you search.
        </Dialog.Description>

        <SearchTextField
          placeholder="Search catalogs or categories…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label="Search catalogs"
        />

        {/* One chip at a time: Favorites is a peer of the categories, not an extra filter. */}
        <Flex gap="1" wrap="wrap" mt="2" aria-label="Filter catalogs">
          <Button
            size="1"
            radius="full"
            variant={!favoritesOnly && !category ? 'solid' : 'soft'}
            color={!favoritesOnly && !category ? undefined : 'gray'}
            aria-pressed={!favoritesOnly && !category}
            onClick={() => {
              setFavoritesOnly(false)
              setCategory(null)
            }}
          >
            All
          </Button>
          <Button
            size="1"
            radius="full"
            variant={favoritesOnly ? 'solid' : 'soft'}
            color={favoritesOnly ? 'amber' : 'gray'}
            aria-pressed={favoritesOnly}
            onClick={() => {
              setFavoritesOnly(prev => !prev)
              setCategory(null)
            }}
          >
            {favoritesOnly ? <IoStar /> : <IoStarOutline />}
            Favorites
          </Button>
          {USE_FAKE_CATALOGS &&
            FAKE_CATEGORIES.map(name => {
              const isActive = category === name
              return (
                <Button
                  key={name}
                  size="1"
                  radius="full"
                  variant={isActive ? 'solid' : 'soft'}
                  color={isActive ? undefined : 'gray'}
                  aria-pressed={isActive}
                  onClick={() => {
                    setCategory(isActive ? null : name)
                    setFavoritesOnly(false)
                  }}
                >
                  {name}
                </Button>
              )
            })}
        </Flex>

        {/* Pinned above the scrolling list so picks stay in view while browsing. */}
        {selectedList.length > 0 && (
          <Box mt="2">
            <button
              type="button"
              className={styles.sectionToggle}
              aria-expanded={isSelectedOpen}
              onClick={() => setIsSelectedOpen(prev => !prev)}
            >
              <Text size="1" color="gray">
                <Flex align="center" gap="1">
                  {isSelectedOpen ? <IoChevronDown /> : <IoChevronForward />}
                  Selected ({selected.size})
                </Flex>
              </Text>
            </button>
            {isSelectedOpen && (
              <ScrollArea
                type="auto"
                scrollbars="vertical"
                className={styles.selectedList}
              >
                {selectedList.map(renderRow)}
              </ScrollArea>
            )}
            <Separator size="4" mt="2" />
          </Box>
        )}
        <ScrollArea type="auto" scrollbars="vertical" className={styles.list}>
          {loading ? (
            <Flex justify="center" p="5">
              <Spinner />
            </Flex>
          ) : unselectedResults.length === 0 ? (
            <Text
              as="p"
              size="2"
              color="gray"
              align="center"
              className={styles.empty}
            >
              {selectedList.length > 0 && results.length > 0
                ? 'All matching catalogs are selected.'
                : emptyMessage}
            </Text>
          ) : (
            unselectedResults.map(renderRow)
          )}
        </ScrollArea>

        {favoritesOnly && (
          <Text as="p" size="1" color="gray" mt="2">
            Find more catalogs to favorite in{' '}
            <Link asChild size="1">
              <RouterLink
                to={DISCOVER_MUSIC_FAVORITES_URL}
                onClick={() => onOpenChange(false)}
              >
                Discover Music →
              </RouterLink>
            </Link>
          </Text>
        )}

        <Flex gap="3" mt="4" align="center" justify="between">
          {/* Nothing selected means every catalog, so there's no separate "all" option. */}
          <Text size="2" color="gray">
            {selected.size > 0 ? (
              <>
                {selected.size} selected ·{' '}
                <Link
                  size="2"
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    setSelected(new Map())
                  }}
                >
                  Unselect all
                </Link>
              </>
            ) : null}
          </Text>
          <Flex gap="3">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleApply}>Apply</Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
